import { type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { DataTableColumnHeader } from './DataTableColumnHeader';
import { type LegacyColumn } from './types';

/**
 * Turns a `Column<T>[]` into TanStack column definitions.
 *
 * There used to be two of these. `adaptLegacyColumns` in types.ts rendered a
 * plain string header while still setting enableSorting, so tables built
 * through DataTableProvider offered sorting with nothing to click; the copy
 * inside DataTable.tsx rendered the sortable header correctly. Both call sites
 * now share this one, so the two paths cannot drift again.
 *
 * On accessors: TanStack resolves sorting, the global filter and CSV export
 * through the accessor, not through `render`. A column whose `key` is a
 * synthetic id ('asset') or a relation ('supplier', holding an object) has
 * nothing scalar to resolve, so those three features misbehave silently while
 * the cell still looks correct — `render` is drawing it. Such a column should
 * declare `accessorFn` to name the value to sort, filter and export by.
 */
export function adaptLegacyColumns<T>(columns: LegacyColumn<T>[]): ColumnDef<T, unknown>[] {
  return columns.map((col) => {
    const accessor = col.accessorFn
      ? { accessorFn: (row: T) => col.accessorFn!(row) as unknown }
      : { accessorKey: col.key };

    return {
      id: col.key,
      ...accessor,
      header: col.sortable
        ? // The column's own className goes to the header too, not just the
          // cells. Without it a numeric column's `text-right` reached the th
          // and stopped there, so every sortable right-aligned column in the
          // product had its label sitting left of the figures beneath it.
          ({ column }) => (
            <DataTableColumnHeader column={column} title={col.header} className={col.className} />
          )
        : col.header,
      cell: ({ row }) => {
        const content = col.render
          ? col.render(row.original)
          : ((v) => (v != null ? String(v) : ''))(row.getValue(col.key));

        const href = col.linkTo?.(row.original);
        if (!href) return content;

        return (
          <Link
            to={href}
            // The row may also be clickable. Both go to the same place, but
            // letting the click reach the row too would push two history
            // entries for one click, so Back would appear not to work.
            onClick={(e) => e.stopPropagation()}
            // Colour, not an underline, and for a CSS reason rather than a
            // taste one. These cells usually render two lines — the identifier
            // and a description under it — and text-decoration propagates to
            // descendants and cannot be switched off by them, so underlining
            // the anchor underlines the description too. Colour is inherited
            // the same way but *can* be overridden, and the second line already
            // sets `text-muted-foreground`, so it keeps its own.
            //
            // It was hover-only underline before, which meant forty tables of
            // identifiers that were real links, navigated correctly, and looked
            // exactly like plain text until a pointer happened to cross them.
            // `block`, because these cells usually contain block children and an
              // inline anchor wrapped around them picks up the line box's
              // leading — sixteen pixels of nothing, on every linked row of every
              // table in the product.
              className="block rounded-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {content}
          </Link>
        );
      },
      enableSorting: col.sortable ?? false,
      meta: { className: col.className },
    } as ColumnDef<T, unknown>;
  });
}
